///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// KML document parsing for drag-and-drop import. Two products map onto
/// existing pipelines: GroundOverlay elements → image ground overlays
/// (step-3 primitive), Placemark geometry → a GeoJSON FeatureCollection
/// for the standard vector import. Styles, NetworkLinks, and gx: extras
/// are out of scope (warned, not fatal).
///

import type { GeoBounds } from "@/redux/dialogSlice"

export interface KmlGroundOverlay {
	name: string
	/** Icon href: archive-relative path (KMZ) or absolute URL */
	href: string
	bounds: GeoBounds
	/** KML color alpha (aabbggrr) as 0..1; 1 when unspecified */
	opacity: number
}

export interface KmlParseResult {
	groundOverlays: KmlGroundOverlay[]
	/** Serialized FeatureCollection; null when the KML has no placemark geometry */
	geojson: string | null
	featureCount: number
	warnings: string[]
}

type GeoJsonGeometry =
	| { type: "Point"; coordinates: number[] }
	| { type: "LineString"; coordinates: number[][] }
	| { type: "Polygon"; coordinates: number[][][] }

/** Parse a KML document (already unzipped) */
export function parseKml(text: string): KmlParseResult {
	const result: KmlParseResult = { groundOverlays: [], geojson: null, featureCount: 0, warnings: [] }
	const doc = new DOMParser().parseFromString(text, "text/xml")
	if (doc.getElementsByTagName("parsererror").length > 0) {
		throw new Error("Not a valid KML document")
	}

	// Ground overlays
	for (const overlay of Array.from(doc.getElementsByTagName("GroundOverlay"))) {
		const href = childText(firstChild(overlay, "Icon"), "href")
		const box = firstChild(overlay, "LatLonBox")
		if (!href || !box) {
			result.warnings.push("GroundOverlay without Icon href or LatLonBox skipped")
			continue
		}
		const north = parseFloat(childText(box, "north") ?? "")
		const south = parseFloat(childText(box, "south") ?? "")
		const east = parseFloat(childText(box, "east") ?? "")
		const west = parseFloat(childText(box, "west") ?? "")
		if (![north, south, east, west].every(isFinite) || north <= south || east <= west) {
			result.warnings.push(`GroundOverlay "${href}" has an invalid LatLonBox`)
			continue
		}
		const rotation = parseFloat(childText(box, "rotation") ?? "0")
		if (isFinite(rotation) && Math.abs(rotation) > 0.01) {
			result.warnings.push(`GroundOverlay "${href}": rotation ${rotation}° isn't supported, placed unrotated`)
		}
		// KML color is aabbggrr hex; only the alpha matters for overlays
		const color = childText(overlay, "color")
		const alpha = color?.length === 8 ? parseInt(color.slice(0, 2), 16) / 255 : 1
		result.groundOverlays.push({
			name: childText(overlay, "name") ?? href.split("/").pop() ?? "Overlay",
			href,
			bounds: {
				west: Math.max(-180, west),
				south: Math.max(-85, south),
				east: Math.min(180, east),
				north: Math.min(85, north),
			},
			opacity: isFinite(alpha) ? Math.max(0.05, Math.min(1, alpha)) : 1,
		})
	}
	if (doc.getElementsByTagName("NetworkLink").length > 0) {
		result.warnings.push("NetworkLink elements are not followed")
	}

	// Placemarks → GeoJSON features
	const features: string[] = []
	for (const placemark of Array.from(doc.getElementsByTagName("Placemark"))) {
		const properties: Record<string, unknown> = {}
		const name = childText(placemark, "name")
		if (name) properties.name = name
		const description = childText(placemark, "description")
		if (description) properties.description = description.slice(0, 2000)
		for (const data of Array.from(placemark.getElementsByTagName("Data"))) {
			const key = data.getAttribute("name")
			const value = childText(data, "value")
			if (key && value !== null) properties[key] = value
		}
		for (const data of Array.from(placemark.getElementsByTagName("SimpleData"))) {
			const key = data.getAttribute("name")
			if (key && data.textContent !== null) properties[key] = data.textContent.trim()
		}

		for (const geometry of placemarkGeometries(placemark)) {
			features.push(JSON.stringify({ type: "Feature", properties, geometry }))
			result.featureCount++
		}
	}
	if (features.length > 0) {
		result.geojson = `{"type":"FeatureCollection","features":[${features.join(",")}]}`
	}
	return result
}

/** Geometries of one placemark (MultiGeometry flattened into siblings) */
function placemarkGeometries(placemark: Element): GeoJsonGeometry[] {
	const geometries: GeoJsonGeometry[] = []
	const visit = (el: Element): void => {
		for (const child of Array.from(el.children)) {
			switch (child.tagName) {
				case "Point": {
					const coords = parseCoordinates(childText(child, "coordinates"))
					if (coords.length > 0) geometries.push({ type: "Point", coordinates: coords[0] })
					break
				}
				case "LineString":
				case "LinearRing": {
					const coords = parseCoordinates(childText(child, "coordinates"))
					if (coords.length >= 2) geometries.push({ type: "LineString", coordinates: coords })
					break
				}
				case "Polygon": {
					const rings: number[][][] = []
					const outer = firstChild(firstChild(child, "outerBoundaryIs"), "LinearRing")
					const outerCoords = parseCoordinates(childText(outer, "coordinates"))
					if (outerCoords.length >= 4) rings.push(outerCoords)
					for (const inner of Array.from(child.getElementsByTagName("innerBoundaryIs"))) {
						const ring = parseCoordinates(childText(firstChild(inner, "LinearRing"), "coordinates"))
						if (ring.length >= 4) rings.push(ring)
					}
					if (rings.length > 0) geometries.push({ type: "Polygon", coordinates: rings })
					break
				}
				case "MultiGeometry":
					visit(child)
					break
			}
		}
	}
	visit(placemark)
	return geometries
}

/** KML coordinates: whitespace-separated "lon,lat[,alt]" tuples */
function parseCoordinates(text: string | null): number[][] {
	if (!text) return []
	const coords: number[][] = []
	for (const token of text.trim().split(/\s+/)) {
		const parts = token.split(",")
		if (parts.length < 2) continue
		const lon = parseFloat(parts[0])
		const lat = parseFloat(parts[1])
		if (isFinite(lon) && isFinite(lat)) coords.push([lon, lat])
	}
	return coords
}

function firstChild(parent: Element | null, tag: string): Element | null {
	if (!parent) return null
	for (const child of Array.from(parent.children)) {
		if (child.tagName === tag) return child
	}
	// Fall back to any descendant (some writers nest inside extra containers)
	const all = parent.getElementsByTagName(tag)
	return all.length > 0 ? all[0] : null
}

function childText(parent: Element | null, tag: string): string | null {
	const el = firstChild(parent, tag)
	return el?.textContent?.trim() ?? null
}
